/**
 * Skills — searchable database of all active + passive skills in
 * Palworld, scraped from palpedia.net (see
 * /app/backend/scripts/scrape_palpedia_skills.py).
 *
 * Two tabs: Active (234 entries) and Passive (309 entries). Active has
 * element + type + exclusive filters; Passive has positive-only + item-
 * only toggles. Both have free-text search.
 */
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { PageHeader } from "@/components/Atoms";
import { ElementChip, PalPortrait } from "@/components/PalAssets";
import { Search, Filter, Zap, Shield, Sparkles, X } from "lucide-react";

const ELEMENTS = ["dark", "dragon", "electric", "fire", "grass", "ground", "ice", "normal", "water"];

/**
 * Map of stats where a *negative* raw value is actually beneficial for
 * the player (and a positive one is harmful). Each entry returns a
 * player-friendly phrase so we display `Hunger drops 20% slower`
 * instead of the cryptic raw `FULL STOMACH DECREASE -20%`.
 *
 * `phrase(magnitude, isBeneficial)` → human-readable string.
 */
const INVERTED_STATS = {
  "FULL STOMACH DECREASE": {
    label: "Hunger drops",
    phrase: (m, good) => `Hunger drops ${m} ${good ? "slower" : "faster"}`,
  },
  "SANITY DECREASE": {
    label: "SAN drops",
    phrase: (m, good) => `SAN drops ${m} ${good ? "slower" : "faster"}`,
  },
  "SHOP BUY PRICE CHANGE": {
    label: "Shop buy prices",
    phrase: (m, good) => `Shop buy prices ${m} ${good ? "cheaper" : "more expensive"}`,
  },
};

/**
 * Normalise a raw `{stat, value}` pair from the passive effects table
 * into a renderable triple `{ label, value, polarity }` where
 *   • `polarity` ∈ "good" | "bad" | "neutral"  – drives the colour
 *   • `label` is the (possibly rewritten) human-readable phrase
 *   • `value` is the raw magnitude/sign (e.g. "+10%", "-20%")
 *
 * For inverted stats the polarity is sign-flipped and the label is
 * rephrased ("FULL STOMACH DECREASE -20%" → "Hunger drops 20% slower").
 */
function formatEffect({ stat, value }) {
  const raw = (value || "").trim();
  const sign = raw.startsWith("+") ? "+" : raw.startsWith("-") ? "-" : "";
  const magnitude = raw.replace(/^[+-]/, "");
  const inverted = INVERTED_STATS[stat];

  if (inverted && sign) {
    const isBeneficial = sign === "-";
    return {
      label: inverted.phrase(magnitude, isBeneficial),
      value: raw,
      polarity: isBeneficial ? "good" : "bad",
      raw_stat: stat,
    };
  }
  return {
    label: stat,
    value: raw,
    polarity: sign === "+" ? "good" : sign === "-" ? "bad" : "neutral",
    raw_stat: stat,
  };
}

const POLARITY_COLOUR = {
  good: "text-emerald-300",
  bad: "text-rose-300",
  neutral: "text-[var(--muted)]",
};

export default function Skills() {
  const toast = useToast();
  const [tab, setTab] = useState("active");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 fade-up" data-testid="skills-page">
      <PageHeader
        title="Skills Database"
        subtitle="Every active and passive skill in Palworld — filter, search, and plan your loadouts."
      />

      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab("active")}
          className={`btn ${tab === "active" ? "" : "btn-secondary"}`}
          data-testid="skills-tab-active"
        >
          <Zap size={12} /> Active Skills
        </button>
        <button
          onClick={() => setTab("passive")}
          className={`btn ${tab === "passive" ? "" : "btn-secondary"}`}
          data-testid="skills-tab-passive"
        >
          <Shield size={12} /> Passive Skills
        </button>
      </div>

      {tab === "active" ? <ActiveSkillsTable toast={toast} /> : <PassiveSkillsTable toast={toast} />}
    </div>
  );
}

function ActiveSkillsTable({ toast }) {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [element, setElement] = useState(null);
  const [type, setType] = useState(null);             // "Shot" | "Melee"
  const [exclusiveOnly, setExclusiveOnly] = useState(false);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("rating");     // "rating" | "power" | "cooldown" | "name"
  const [selected, setSelected] = useState(null);     // skill object opened in modal

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (element) params.set("element", element);
    if (type) params.set("type", type);
    if (exclusiveOnly) params.set("exclusive", "true");
    if (q.trim()) params.set("q", q.trim());
    api.get(`/skills/active?${params.toString()}`)
      .then(({ data }) => setSkills(data.skills || []))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [element, type, exclusiveOnly, q, toast]);

  const sorted = useMemo(() => {
    const arr = [...skills];
    arr.sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "power") return (b.power || 0) - (a.power || 0);
      if (sortBy === "cooldown") return (a.cooldown || 0) - (b.cooldown || 0);
      // rating desc by default, nulls last
      return (b.rating || -1) - (a.rating || -1);
    });
    return arr;
  }, [skills, sortBy]);

  return (
    <div data-testid="active-skills">
      <FiltersPanel
        q={q}
        setQ={setQ}
        searchTestId="active-skills-search"
        extra={
          <>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Filter size={11} className="text-[var(--muted)]" />
              <span className="text-[10px] uppercase tracking-wider text-[var(--muted)] mr-1">Element</span>
              <FilterChip active={element === null} onClick={() => setElement(null)} testId="element-all">
                All
              </FilterChip>
              {ELEMENTS.map((el) => (
                <button
                  key={el}
                  onClick={() => setElement(element === el ? null : el)}
                  className={`p-0.5 rounded-full border ${
                    element === el
                      ? "border-[var(--teal)] bg-[rgba(0,229,255,0.06)]"
                      : "border-white/10 opacity-70 hover:opacity-100"
                  }`}
                  title={el}
                  data-testid={`active-element-${el}`}
                >
                  <ElementChip element={el} size={14} showLabel={false} />
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              <span className="text-[10px] uppercase tracking-wider text-[var(--muted)] mr-1">Type</span>
              <FilterChip active={type === null} onClick={() => setType(null)} testId="type-all">All</FilterChip>
              <FilterChip active={type === "Shot"} onClick={() => setType("Shot")} testId="type-shot">Shot</FilterChip>
              <FilterChip active={type === "Melee"} onClick={() => setType("Melee")} testId="type-melee">Melee</FilterChip>
              <span className="mx-1 text-white/10">·</span>
              <FilterChip
                active={exclusiveOnly}
                onClick={() => setExclusiveOnly(!exclusiveOnly)}
                testId="exclusive-toggle"
              >
                ★ Exclusive only
              </FilterChip>
              <span className="mx-1 text-white/10">·</span>
              <span className="text-[10px] uppercase tracking-wider text-[var(--muted)] mr-1">Sort</span>
              {[
                { id: "rating", label: "Rating" },
                { id: "power", label: "Power" },
                { id: "cooldown", label: "Cooldown" },
                { id: "name", label: "A–Z" },
              ].map((opt) => (
                <FilterChip
                  key={opt.id}
                  active={sortBy === opt.id}
                  onClick={() => setSortBy(opt.id)}
                  testId={`sort-${opt.id}`}
                >
                  {opt.label}
                </FilterChip>
              ))}
            </div>
          </>
        }
      />

      <div className="text-[10px] text-[var(--muted)] mb-3" data-testid="active-skills-count">
        {loading ? "Loading…" : `${sorted.length} skill${sorted.length === 1 ? "" : "s"} matching`}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {sorted.map((s) => (
          <ActiveSkillCard key={s.name} skill={s} onClick={() => setSelected(s)} />
        ))}
      </div>
      {!loading && sorted.length === 0 && (
        <div className="py-12 text-center text-[var(--muted)] text-sm">
          No skills match the current filters.
        </div>
      )}
      {selected && <ActiveSkillModal skill={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function ActiveSkillCard({ skill, onClick }) {
  const rating = skill.rating;
  const ratingColour =
    rating == null ? "text-[var(--muted)]"
    : rating >= 5 ? "glow-gold"
    : rating >= 3 ? "text-emerald-300"
    : rating >= 2 ? "text-amber-300"
    : "text-rose-300";
  return (
    <article
      className={`glass-card p-4 text-left transition-transform hover:-translate-y-0.5 hover:ring-1 hover:ring-[var(--teal)]/40 cursor-pointer ${skill.exclusive ? "ring-1 ring-[var(--gold)]/30" : ""}`}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } }}
      role="button"
      tabIndex={0}
      data-testid={`active-skill-${skill.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <header className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-semibold truncate flex items-center gap-1.5">
            <ElementChip element={skill.element} size={14} showLabel={false} />
            <span className="truncate">{skill.name}</span>
          </h4>
          <div className="text-[10px] text-[var(--muted)] uppercase tracking-wider mt-0.5">
            {skill.type} · {skill.element}
          </div>
          {skill.exclusive && (
            <span
              className="inline-flex items-center gap-1 mt-1 text-[9px] tracking-[0.18em] uppercase text-[var(--gold)] border border-[var(--gold)]/40 bg-[rgba(255,215,128,0.06)] pl-0.5 pr-1.5 py-0.5 rounded-full max-w-full"
              title={skill.exclusive_pal ? `Exclusive to ${skill.exclusive_pal}` : "Exclusive — only one pal can learn this"}
              data-testid={`active-skill-${skill.name.toLowerCase().replace(/\s+/g, "-")}-exclusive`}
            >
              {skill.exclusive_pal ? (
                <PalPortrait
                  name={skill.exclusive_pal}
                  size="w-4 h-4"
                  className="ring-1 ring-[var(--gold)]/40 shrink-0"
                />
              ) : (
                <span className="px-0.5">★</span>
              )}
              <span className="shrink-0">Exclusive</span>
              {skill.exclusive_pal && (
                <span className="text-[var(--muted)] normal-case tracking-normal truncate">· {skill.exclusive_pal}</span>
              )}
            </span>
          )}
        </div>
        {rating != null && (
          <span
            className={`text-xs font-mono ${ratingColour} shrink-0`}
            title="PWR / CLD rating"
          >
            {rating.toFixed(2)}
          </span>
        )}
      </header>

      <div className="grid grid-cols-3 gap-2 mb-2 text-[10px]">
        <Stat label="PWR" value={skill.power} colour="text-[var(--gold)]" />
        <Stat label="CLD" value={skill.cooldown} colour="text-rose-300" />
        <Stat
          label="RNG"
          value={skill.range_min != null ? `${skill.range_min}-${skill.range_max}` : "—"}
        />
      </div>

      {skill.description && (
        <p className="text-[11px] text-[var(--muted)] leading-snug">{skill.description}</p>
      )}
      {skill.inflicts && (
        <div className="text-[10px] mt-2">
          <span className="text-[var(--muted)] uppercase tracking-wider mr-1">Inflicts</span>
          <span className="text-amber-300">{skill.inflicts}</span>
        </div>
      )}
    </article>
  );
}

function PassiveSkillsTable({ toast }) {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [positiveOnly, setPositiveOnly] = useState(true);
  // Source: "pal" (default), "equipment", "human", "all"
  const [source, setSource] = useState("pal");
  const [rank, setRank] = useState(null);             // null | 1 | 2 | 3 | 4 | -1 | -2 | -3
  const [selected, setSelected] = useState(null);     // passive object opened in modal

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    // Rank filter takes precedence over positive_only — picking a negative
    // rank explicitly opts the user into seeing debuffs.
    if (rank !== null) {
      params.set("rank", String(rank));
    } else if (positiveOnly) {
      params.set("positive_only", "true");
    }
    params.set("source", source);
    if (q.trim()) params.set("q", q.trim());
    api.get(`/skills/passive?${params.toString()}`)
      .then(({ data }) => setSkills(data.skills || []))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [q, positiveOnly, source, rank, toast]);

  return (
    <div data-testid="passive-skills">
      <FiltersPanel
        q={q}
        setQ={setQ}
        searchTestId="passive-skills-search"
        extra={
          <>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Filter size={11} className="text-[var(--muted)]" />
              <span className="text-[10px] uppercase tracking-wider text-[var(--muted)] mr-1">Tier</span>
              <FilterChip active={rank === null} onClick={() => setRank(null)} testId="passive-rank-all">
                All Tiers
              </FilterChip>
              <FilterChip active={rank === 4} onClick={() => setRank(rank === 4 ? null : 4)} testId="passive-rank-4">
                <span className="text-[var(--gold)]">★★★★ Rank 4</span>
              </FilterChip>
              <FilterChip active={rank === 3} onClick={() => setRank(rank === 3 ? null : 3)} testId="passive-rank-3">
                <span className="text-[var(--gold)]">★★★ Rank 3</span>
              </FilterChip>
              <FilterChip active={rank === 2} onClick={() => setRank(rank === 2 ? null : 2)} testId="passive-rank-2">
                ★★ Rank 2
              </FilterChip>
              <FilterChip active={rank === 1} onClick={() => setRank(rank === 1 ? null : 1)} testId="passive-rank-1">
                ★ Rank 1
              </FilterChip>
              <FilterChip active={rank === -1} onClick={() => setRank(rank === -1 ? null : -1)} testId="passive-rank-neg1">
                <span className="text-rose-400">▼ Rank -1</span>
              </FilterChip>
              <FilterChip active={rank === -2} onClick={() => setRank(rank === -2 ? null : -2)} testId="passive-rank-neg2">
                <span className="text-rose-400">▼▼ Rank -2</span>
              </FilterChip>
              <FilterChip active={rank === -3} onClick={() => setRank(rank === -3 ? null : -3)} testId="passive-rank-neg3">
                <span className="text-rose-400">▼▼▼ Rank -3</span>
              </FilterChip>
            </div>

            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              <FilterChip active={positiveOnly && rank === null} onClick={() => setPositiveOnly(!positiveOnly)} testId="positive-only">
                Positive only
              </FilterChip>
              <span className="mx-1 text-white/10">·</span>
              <span className="text-[10px] uppercase tracking-wider text-[var(--muted)] mr-1">Source</span>
              <FilterChip active={source === "pal"} onClick={() => setSource("pal")} testId="source-pal">Pal passives</FilterChip>
              <FilterChip active={source === "equipment"} onClick={() => setSource("equipment")} testId="source-equipment">Equipment-only</FilterChip>
              <FilterChip active={source === "human"} onClick={() => setSource("human")} testId="source-human">Human-only</FilterChip>
              <FilterChip active={source === "all"} onClick={() => setSource("all")} testId="source-all">Show all</FilterChip>
            </div>
          </>
        }
      />

      <div className="text-[10px] text-[var(--muted)] mb-3" data-testid="passive-skills-count">
        {loading ? "Loading…" : `${skills.length} passive${skills.length === 1 ? "" : "s"} matching`}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {skills.map((s, idx) => (
          <PassiveSkillCard key={`${s.name}-${s.rank}-${idx}`} skill={s} onClick={() => setSelected(s)} />
        ))}
      </div>
      {!loading && skills.length === 0 && (
        <div className="py-12 text-center text-[var(--muted)] text-sm">
          No passives match the current filters.
        </div>
      )}
      {selected && <PassiveSkillModal skill={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function PassiveSkillCard({ skill, onClick }) {
  const tierColour = skill.rank > 0
    ? "text-[var(--gold)]"
    : skill.rank < 0 ? "text-rose-400" : "text-[var(--muted)]";
  const iconUrl = skill.icon
    ? (skill.icon.startsWith("http") ? skill.icon : `${process.env.REACT_APP_BACKEND_URL}${skill.icon}`)
    : null;
  // For equipment passives we render the granting items as visual chips,
  // so strip the redundant "ITEM ...This skill can be found on N items: ..."
  // prefix from the description.
  const cleanDescription = skill.item_only && skill.description
    ? skill.description
        .replace(/^ITEM\s*/i, "")
        .replace(/\s*This skill can be found on \d+ items?:.*$/i, "")
        .trim()
    : skill.description;
  // Clicking on the inner <details> chip-list should expand items, not the
  // outer card modal — stop propagation on that wrapper.
  const stop = (e) => e.stopPropagation();
  return (
    <article
      className="glass-card p-4 text-left transition-transform hover:-translate-y-0.5 hover:ring-1 hover:ring-[var(--teal)]/40 cursor-pointer"
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } }}
      role="button"
      tabIndex={0}
      data-testid={`passive-skill-${skill.name.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <header className="flex items-start justify-between gap-2 mb-1">
        <h4 className="text-sm font-semibold flex items-center gap-1.5 min-w-0 flex-1">
          {iconUrl ? (
            <img
              src={iconUrl}
              alt={`Rank ${skill.rank}`}
              title={`Rank ${skill.rank}`}
              className="w-5 h-5 object-contain shrink-0"
              loading="lazy"
              draggable={false}
              data-testid={`passive-rank-icon-${skill.rank}`}
            />
          ) : (
            <Sparkles size={12} className="text-[var(--teal)] shrink-0" />
          )}
          <span className="truncate">{skill.name}</span>
        </h4>
        <span className={`text-[10px] font-mono ${tierColour} shrink-0`} title={`Rank ${skill.rank}`}>
          {skill.rank > 0 ? `+${skill.rank}` : skill.rank}
        </span>
      </header>
      {(skill.source === "human" || skill.item_only) && (
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          {skill.source === "human" && (
            <span
              className="text-[9px] tracking-[0.18em] uppercase text-amber-300 border border-amber-300/30 bg-amber-300/5 px-1.5 py-0.5 rounded-full"
              title="Human-only — exclusive to NPC faction leaders, never rolled on a pal"
            >
              Human-only
            </span>
          )}
          {skill.item_only && (
            <span
              className="text-[9px] tracking-[0.18em] uppercase text-[var(--muted)] border border-white/10 bg-white/5 px-1.5 py-0.5 rounded-full"
              title="Only available on equipped gear"
            >
              Equipment
            </span>
          )}
        </div>
      )}
      {skill.granted_by_items?.length > 0 && (
        <details
          className="mb-2 group rounded-lg bg-black/20 border border-white/5 overflow-hidden"
          open={skill.granted_by_items.length <= 4}
          onClick={stop}
        >
          <summary className="cursor-pointer list-none px-2 py-1.5 text-[10px] uppercase tracking-wider text-[var(--muted)] hover:text-white/80 transition-colors flex items-center justify-between">
            <span>Found on {skill.granted_by_items.length} item{skill.granted_by_items.length === 1 ? "" : "s"}</span>
            <span className="group-open:rotate-90 transition-transform text-[var(--muted)]">▶</span>
          </summary>
          <ul className="px-2 pb-2 pt-1 space-y-1 max-h-44 overflow-y-auto">
            {skill.granted_by_items.map((it) => (
              <li
                key={it.name}
                className="flex items-center gap-2 text-[11px]"
                data-testid={`passive-item-${it.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${it.image}`}
                  alt={it.name}
                  className="w-7 h-7 rounded object-contain bg-black/40 ring-1 ring-white/10 shrink-0"
                  loading="lazy"
                  draggable={false}
                />
                <span className="truncate text-white/85" title={it.name}>{it.name}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {cleanDescription && (
        <p className="text-[11px] text-[var(--muted)] leading-snug mb-2">{cleanDescription}</p>
      )}
      {skill.effects?.length > 0 && (
        <ul className="text-[10px] space-y-0.5">
          {skill.effects.map((e, i) => {
            const fx = formatEffect(e);
            return (
              <li key={i} className="flex justify-between gap-2" title={fx.raw_stat}>
                <span className="text-[var(--muted)] uppercase tracking-wider truncate">{fx.label}</span>
                <span className={`font-mono ${POLARITY_COLOUR[fx.polarity]}`}>
                  {fx.value}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
}

function FiltersPanel({ q, setQ, searchTestId, extra }) {
  return (
    <div className="glass-card p-4 mb-4 space-y-3">
      <div className="relative">
        <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
        <input
          type="text"
          className="input pl-8"
          placeholder="Search by name, description, or effect…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid={searchTestId}
        />
      </div>
      {extra}
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
        active
          ? "border-[var(--teal)] text-[var(--teal)] bg-[rgba(0,229,255,0.06)]"
          : "border-white/10 text-[var(--muted)] hover:text-[var(--bone)]"
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function Stat({ label, value, colour }) {
  return (
    <div className="text-center">
      <div className="text-[9px] uppercase tracking-wider text-[var(--muted)]">{label}</div>
      <div className={`font-mono ${colour || "text-[var(--bone)]"}`}>{value ?? "—"}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Modals                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Shared modal shell — fixed backdrop, click-to-close, Esc-to-close, glass card.
 * Children render the content; the caller controls layout and testids.
 */
function SkillModalShell({ onClose, children, testId, ringClass = "" }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4 backdrop-blur-sm fade-up"
      onClick={onClose}
      data-testid={testId}
    >
      <div
        className={`glass-card relative max-w-2xl w-full max-h-[88vh] overflow-y-auto ${ringClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[var(--muted)] hover:text-[var(--bone)] p-1 rounded-full hover:bg-white/5 z-10"
          aria-label="Close"
          data-testid={`${testId}-close`}
        >
          <X size={18} />
        </button>
        {children}
      </div>
    </div>
  );
}

/** Expanded view of an active skill — bigger element art, full stats grid. */
function ActiveSkillModal({ skill, onClose }) {
  const rating = skill.rating;
  const ratingColour =
    rating == null ? "text-[var(--muted)]"
    : rating >= 5 ? "glow-gold"
    : rating >= 3 ? "text-emerald-300"
    : rating >= 2 ? "text-amber-300"
    : "text-rose-300";
  const slug = skill.name.toLowerCase().replace(/\s+/g, "-");
  return (
    <SkillModalShell
      onClose={onClose}
      testId={`active-skill-modal-${slug}`}
      ringClass={skill.exclusive ? "ring-1 ring-[var(--gold)]/40" : ""}
    >
      <div className="p-6 sm:p-8">
        {/* Hero header — large element badge + name */}
        <div className="flex items-start gap-4 sm:gap-6 mb-6">
          {skill.exclusive && skill.exclusive_pal ? (
            <PalPortrait
              name={skill.exclusive_pal}
              size="w-24 h-24 sm:w-28 sm:h-28 shrink-0"
              className="ring-2 ring-[var(--gold)]/50"
            />
          ) : (
            <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 rounded-xl bg-black/40 ring-1 ring-white/10 flex items-center justify-center">
              <ElementChip element={skill.element} size={64} showLabel={false} />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-1" data-testid="active-skill-modal-name">
              {skill.name}
            </h2>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <ElementChip element={skill.element} size={16} showLabel={true} />
              <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] border border-white/10 px-2 py-0.5 rounded-full">
                {skill.type}
              </span>
              {skill.exclusive && (
                <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--gold)] border border-[var(--gold)]/40 bg-[rgba(255,215,128,0.06)] px-2 py-0.5 rounded-full">
                  ★ Exclusive{skill.exclusive_pal ? ` · ${skill.exclusive_pal}` : ""}
                </span>
              )}
            </div>
            {rating != null && (
              <div className="text-xs text-[var(--muted)]">
                Rating <span className={`font-mono text-base ${ratingColour}`}>{rating.toFixed(2)}</span>
                <span className="text-[var(--muted)] ml-1">(power-vs-cooldown)</span>
              </div>
            )}
          </div>
        </div>

        {/* Stats grid — generous spacing */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <BigStat label="Power" value={skill.power} colour="text-[var(--gold)]" />
          <BigStat label="Cooldown" value={skill.cooldown != null ? `${skill.cooldown}s` : null} colour="text-rose-300" />
          <BigStat
            label="Range"
            value={skill.range_min != null ? `${skill.range_min}–${skill.range_max}` : null}
          />
        </div>

        {/* Description */}
        {skill.description && (
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-1.5">Description</div>
            <p className="text-sm leading-relaxed text-white/85">{skill.description}</p>
          </div>
        )}

        {/* Inflicts (status effect) */}
        {skill.inflicts && (
          <div className="mb-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-1.5">Inflicts</div>
            <span className="inline-block text-sm text-amber-300 border border-amber-300/30 bg-amber-300/5 px-2.5 py-1 rounded-full">
              {skill.inflicts}
            </span>
          </div>
        )}
      </div>
    </SkillModalShell>
  );
}

/** Expanded view of a passive skill — bigger rank arrow, item list, stat table. */
function PassiveSkillModal({ skill, onClose }) {
  const tierColour = skill.rank > 0
    ? "text-[var(--gold)]"
    : skill.rank < 0 ? "text-rose-400" : "text-[var(--muted)]";
  const iconUrl = skill.icon
    ? (skill.icon.startsWith("http") ? skill.icon : `${process.env.REACT_APP_BACKEND_URL}${skill.icon}`)
    : null;
  const cleanDescription = skill.item_only && skill.description
    ? skill.description
        .replace(/^ITEM\s*/i, "")
        .replace(/\s*This skill can be found on \d+ items?:.*$/i, "")
        .trim()
    : skill.description;
  const slug = skill.name.toLowerCase().replace(/\s+/g, "-");
  return (
    <SkillModalShell onClose={onClose} testId={`passive-skill-modal-${slug}`}>
      <div className="p-6 sm:p-8">
        {/* Hero header — large rank arrow + name */}
        <div className="flex items-start gap-4 sm:gap-6 mb-6">
          <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 rounded-xl bg-black/40 ring-1 ring-white/10 flex items-center justify-center">
            {iconUrl ? (
              <img
                src={iconUrl}
                alt={`Rank ${skill.rank}`}
                title={`Rank ${skill.rank}`}
                className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                draggable={false}
                data-testid="passive-skill-modal-rank-icon"
              />
            ) : (
              <Sparkles size={48} className="text-[var(--teal)]" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-1" data-testid="passive-skill-modal-name">
              {skill.name}
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-sm font-mono ${tierColour}`}>
                Rank {skill.rank > 0 ? `+${skill.rank}` : skill.rank}
              </span>
              {skill.source === "human" && (
                <span className="text-[10px] tracking-[0.18em] uppercase text-amber-300 border border-amber-300/30 bg-amber-300/5 px-2 py-0.5 rounded-full">
                  Human-only
                </span>
              )}
              {skill.item_only && (
                <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--muted)] border border-white/10 bg-white/5 px-2 py-0.5 rounded-full">
                  Equipment
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {cleanDescription && (
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-1.5">Description</div>
            <p className="text-sm leading-relaxed text-white/85">{cleanDescription}</p>
          </div>
        )}

        {/* Effects breakdown */}
        {skill.effects?.length > 0 && (
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-1.5">Effects</div>
            <ul className="grid sm:grid-cols-2 gap-1.5">
              {skill.effects.map((e, i) => {
                const fx = formatEffect(e);
                return (
                  <li
                    key={i}
                    className="flex justify-between items-center gap-2 px-3 py-1.5 rounded bg-black/20 border border-white/5"
                    title={fx.raw_stat}
                  >
                    <span className="text-[11px] text-[var(--muted)] uppercase tracking-wider truncate">{fx.label}</span>
                    <span className={`font-mono text-sm ${POLARITY_COLOUR[fx.polarity]}`}>
                      {fx.value}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Granting items (equipment passives) */}
        {skill.granted_by_items?.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-1.5">
              Found on {skill.granted_by_items.length} item{skill.granted_by_items.length === 1 ? "" : "s"}
            </div>
            <ul className="grid sm:grid-cols-2 gap-1.5 max-h-72 overflow-y-auto">
              {skill.granted_by_items.map((it) => (
                <li
                  key={it.name}
                  className="flex items-center gap-2.5 px-2 py-1.5 rounded bg-black/20 border border-white/5"
                  data-testid={`passive-modal-item-${it.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${it.image}`}
                    alt={it.name}
                    className="w-10 h-10 rounded object-contain bg-black/40 ring-1 ring-white/10 shrink-0"
                    loading="lazy"
                    draggable={false}
                  />
                  <span className="text-xs text-white/85 truncate" title={it.name}>{it.name}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </SkillModalShell>
  );
}

/** Larger stat tile used inside the expanded skill modals. */
function BigStat({ label, value, colour }) {
  return (
    <div className="rounded-lg bg-black/30 border border-white/5 px-3 py-3 text-center">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mb-1">{label}</div>
      <div className={`font-mono text-2xl ${colour || "text-[var(--bone)]"}`}>{value ?? "—"}</div>
    </div>
  );
}
